import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import { Loading } from '../components/Loading';
import type { MonthlyVolume } from '../api/types';

const numberFormat = new Intl.NumberFormat('en-AU');
const currencyFormat = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});
const compactCurrencyFormat = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatCurrency = (value: number) => (value >= 100_000 ? compactCurrencyFormat.format(value) : currencyFormat.format(value));
const formatMonth = (month: string) => {
  const [year, m] = month.split('-');
  return new Date(Number(year), Number(m) - 1, 1).toLocaleDateString('en-AU', { month: 'short' });
};

/** Rounds up to a clean axis maximum: 1/2/5 × a power of ten. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= value) return candidate;
  }
  return 10 * magnitude;
}

const CHART_WIDTH = 720;
const CHART_HEIGHT = 200;
const MARGIN = { top: 20, right: 12, bottom: 24, left: 32 };

function MonthlyVolumeChart({ data }: { data: MonthlyVolume[] }) {
  if (data.length === 0) return <p className="muted">No complaints lodged yet.</p>;

  const innerWidth = CHART_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
  const max = niceMax(Math.max(...data.map((d) => d.total)));
  const bandWidth = innerWidth / data.length;
  const barWidth = Math.min(24, bandWidth * 0.6);
  const yFor = (value: number) => innerHeight - (value / max) * innerHeight;
  const lastIndex = data.length - 1;

  const ticks = [0, max / 2, max];

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img" aria-label="Complaints lodged per month, last 12 months">
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line x1={0} x2={innerWidth} y1={yFor(tick)} y2={yFor(tick)} className="chart-grid-line" />
              <text x={-8} y={yFor(tick)} dy={3} textAnchor="end" className="chart-axis-label">
                {Math.round(tick)}
              </text>
            </g>
          ))}

          {data.map((d, i) => {
            const x = i * bandWidth + (bandWidth - barWidth) / 2;
            const barHeight = Math.max(0, innerHeight - yFor(d.total));
            const y = yFor(d.total);
            const r = Math.min(4, barHeight);
            const path =
              barHeight === 0
                ? ''
                : `M${x},${innerHeight}
                   L${x},${y + r}
                   Q${x},${y} ${x + r},${y}
                   L${x + barWidth - r},${y}
                   Q${x + barWidth},${y} ${x + barWidth},${y + r}
                   L${x + barWidth},${innerHeight}
                   Z`;

            return (
              <g key={d.month}>
                {barHeight > 0 && <path d={path} className="chart-bar" />}
                {i === lastIndex && (
                  <text x={x + barWidth / 2} y={y - 6} textAnchor="middle" className="chart-value-label">
                    {d.total}
                  </text>
                )}
                <text x={x + barWidth / 2} y={innerHeight + 16} textAnchor="middle" className="chart-axis-label">
                  {formatMonth(d.month)}
                </text>
              </g>
            );
          })}

          <line x1={0} x2={innerWidth} y1={innerHeight} y2={innerHeight} className="chart-grid-line" />
        </g>
      </svg>
    </div>
  );
}

export function Reports() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'overview'],
    queryFn: api.reportsOverview,
  });

  if (isLoading) return <Loading fullscreen message="Loading reports…" />;
  if (isError) {
    return (
      <div className="page">
        <div className="notice error">{(error as Error).message}</div>
      </div>
    );
  }
  if (!data) return null;

  const { totals, monthly_volumes, issue_counts, staff_workload } = data;

  return (
    <div className="page">
      <h1 className="page-title">Reports</h1>
      <p className="page-subtitle">A rollup of every complaint in the system — nothing here is filtered by who lodged it.</p>

      <div className="stat-grid">
        <div className="stat-tile">
          <div className="stat-label">Open complaints</div>
          <div className="stat-value">{numberFormat.format(totals.open_count)}</div>
        </div>
        <div className="stat-tile">
          <div className="stat-label">Past IDR deadline</div>
          <div className={`stat-value${totals.overdue_count > 0 ? ' danger' : ''}`}>
            {numberFormat.format(totals.overdue_count)}
          </div>
        </div>
        <div className="stat-tile">
          <div className="stat-label">Avg. days to resolve</div>
          <div className="stat-value">
            {totals.avg_days_to_resolve === null ? '—' : totals.avg_days_to_resolve.toFixed(1)}
          </div>
        </div>
        <div className="stat-tile">
          <div className="stat-label">Total monetary remedy</div>
          <div className="stat-value">{formatCurrency(totals.total_monetary_remedy)}</div>
        </div>
      </div>

      <div className="card">
        <h2 className="section-title">Complaints lodged, last 12 months</h2>
        <MonthlyVolumeChart data={monthly_volumes} />

        <div className="table-wrap" style={{ marginTop: 18 }}>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Lodged</th>
                <th>Open</th>
                <th>Closed</th>
                <th>Sensitive</th>
                <th>Avg. days to resolve</th>
                <th>Monetary remedy</th>
              </tr>
            </thead>
            <tbody>
              {monthly_volumes.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td>{numberFormat.format(m.total)}</td>
                  <td>{numberFormat.format(m.open_count)}</td>
                  <td>{numberFormat.format(m.closed_count)}</td>
                  <td>{numberFormat.format(m.sensitive_count)}</td>
                  <td>{m.avg_days_to_resolve === null ? '—' : m.avg_days_to_resolve.toFixed(1)}</td>
                  <td>{formatCurrency(m.total_monetary_remedy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2 className="section-title">Top issues (primary code)</h2>
        {issue_counts.length === 0 ? (
          <p className="muted">No complaints have been coded to an ASIC issue yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Issue</th>
                  <th>Total</th>
                  <th>Open</th>
                  <th>Closed</th>
                  <th>Avg. days to resolve</th>
                </tr>
              </thead>
              <tbody>
                {issue_counts.map((row) => (
                  <tr key={row.code}>
                    <td className="muted">{row.category}</td>
                    <td>{row.label}</td>
                    <td>{numberFormat.format(row.complaint_count)}</td>
                    <td>{numberFormat.format(row.open_count)}</td>
                    <td>{numberFormat.format(row.closed_count)}</td>
                    <td>{row.avg_days_to_resolve === null ? '—' : row.avg_days_to_resolve.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="section-title">Staff workload</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Staff</th>
                <th>Total lodged</th>
                <th>Open</th>
                <th>Closed</th>
                <th>Sensitive</th>
                <th>Most recent</th>
              </tr>
            </thead>
            <tbody>
              {staff_workload.map((row) => (
                <tr key={row.staff_email}>
                  <td>{row.staff_email}</td>
                  <td>{numberFormat.format(row.total_lodged)}</td>
                  <td>{numberFormat.format(row.open_complaints)}</td>
                  <td>{numberFormat.format(row.closed_complaints)}</td>
                  <td>{numberFormat.format(row.sensitive_complaints)}</td>
                  <td>{row.most_recent_complaint ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
